/**
 * Video Recording Utilities
 *
 * Handles camera access and video recording using MediaRecorder API
 * with mobile-optimized settings and codec fallbacks.
 */

import { debugLog } from './debugUtilities';
export interface CameraConstraints {
  facingMode?: 'user' | 'environment';
  width?: { ideal: number };
  height?: { ideal: number };
  frameRate?: { ideal: number };
}

export interface RecordingOptions {
  mimeType?: string;
  videoBitsPerSecond?: number;
}

/**
 * Device performance tiers for adaptive quality
 */
export type DevicePerformanceTier = 'high' | 'mid' | 'low';

/**
 * Detect device performance tier based on hardware capabilities
 */
export function detectDevicePerformance(): DevicePerformanceTier {
  // Check for device memory (GB)
  const memory = (navigator as any).deviceMemory;

  // Check for hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 2;

  // Check User Agent for specific devices
  const ua = navigator.userAgent.toLowerCase();

  // iOS detection
  const isOlderiPhone = /iphone (6|7|8|se|x)/.test(ua); // iPhone 6-X are mid-range
  const isVeryOldPhone = /iphone (4|5)/.test(ua); // iPhone 4/5 are low-end

  // Android detection
  const isAndroid = /android/.test(ua);
  const androidVersion = ua.match(/android (\d+)/)?.[1];
  const androidVersionNum = androidVersion ? parseInt(androidVersion) : 0;

  // Old Android devices (pre-Android 9, 2019 and earlier)
  const isOldAndroid = isAndroid && androidVersionNum > 0 && androidVersionNum < 9;

  // Very old Android (Android 5-7, 2014-2017)
  const isVeryOldAndroid = isAndroid && androidVersionNum > 0 && androidVersionNum < 8;

  // Common budget Android brands/devices (these often have weaker hardware)
  const isBudgetAndroid = isAndroid && (
    /samsung.*(galaxy (j|a|m)\d+|sm-[ajm]\d+)/.test(ua) || // Samsung J/A/M series
    /moto.*(e|g)\d+/.test(ua) || // Moto E/G series
    /redmi|xiaomi/.test(ua) || // Xiaomi budget lines
    /huawei.*(lite|y\d+)/.test(ua) // Huawei budget lines
  );

  debugLog('[DeviceDetection] Device info:', {
    memory,
    cores,
    isAndroid,
    androidVersion: androidVersionNum,
    isBudgetAndroid,
    ua: navigator.userAgent
  });

  // Very old devices (iPhone 4/5, Android 5-7, <2GB RAM, <2 cores)
  if (isVeryOldPhone || isVeryOldAndroid || (memory && memory < 2) || cores < 2) {
    debugLog('[DeviceDetection] Tier: LOW (very old device)');
    return 'low';
  }

  // Mid-range devices (iPhone 6-X, Android 8-10, old budget phones, 2-4GB RAM, 2-4 cores)
  if (isOlderiPhone || isOldAndroid || isBudgetAndroid || (memory && memory < 4) || cores < 4) {
    debugLog('[DeviceDetection] Tier: MID (older/budget device)');
    return 'mid';
  }

  // High-end devices (modern iPhones/Android, 4GB+ RAM, 4+ cores)
  debugLog('[DeviceDetection] Tier: HIGH (modern device)');
  return 'high';
}

/**
 * Get camera constraints based on device performance
 */
export function getAdaptiveCameraConstraints(tier?: DevicePerformanceTier): MediaStreamConstraints {
  const performanceTier = tier || detectDevicePerformance();

  const constraints: Record<DevicePerformanceTier, MediaStreamConstraints> = {
    high: {
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 }
      },
      audio: false
    },
    mid: {
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      },
      audio: false
    },
    low: {
      video: {
        facingMode: 'environment',
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 24 }
      },
      audio: false
    }
  };

  const selected = constraints[performanceTier];
  debugLog('[CameraConstraints] Using tier:', performanceTier, selected);
  return selected;
}

const DEFAULT_CAMERA_CONSTRAINTS: MediaStreamConstraints = getAdaptiveCameraConstraints();

const DEFAULT_RECORDING_OPTIONS: RecordingOptions = {
  videoBitsPerSecond: 2500000 // 2.5 Mbps
};

/**
 * Get supported MIME type for MediaRecorder
 */
function getSupportedMimeType(): string {
  // Prioritize MP4 for better backend compatibility
  const types = [
    'video/mp4',
    'video/mp4;codecs=h264',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm'
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      debugLog('[VideoRecorder] Using MIME type:', type);
      return type;
    }
  }

  // Fallback to default
  console.warn('[VideoRecorder] No supported MIME type found, using default');
  return 'video/webm';
}

/**
 * Video Recorder class
 * Manages camera access, recording, and blob generation
 */
export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startTime: number | null = null;
  private onDataAvailableCallback: ((blob: Blob) => void) | null = null;

  /**
   * Request camera access and start preview
   * @param constraints Camera constraints (defaults to back camera, 1080p, 30fps)
   * @returns MediaStream for camera feed
   */
  async startCamera(constraints: MediaStreamConstraints = DEFAULT_CAMERA_CONSTRAINTS): Promise<MediaStream> {
    try {
      debugLog('[VideoRecorder] Requesting camera access...');

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      debugLog('[VideoRecorder] Camera access granted');
      debugLog('[VideoRecorder] Video tracks:', this.stream.getVideoTracks().map(t => ({
        label: t.label,
        settings: t.getSettings()
      })));

      return this.stream;
    } catch (error) {
      console.error('[VideoRecorder] Camera access failed:', error);

      let errorMessage = 'Camera access denied';
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Camera permission denied. Please allow camera access in your browser settings.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No camera found on this device.';
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Camera is already in use by another application.';
        } else {
          errorMessage = error.message;
        }
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Start recording video
   * @param stream MediaStream from camera
   * @param onDataAvailable Callback when recording is complete
   * @param options Recording options (codec, bitrate)
   */
  startRecording(
    stream: MediaStream,
    onDataAvailable: (blob: Blob) => void,
    options: RecordingOptions = DEFAULT_RECORDING_OPTIONS
  ): void {
    try {
      const mimeType = getSupportedMimeType();

      const recorderOptions: MediaRecorderOptions = {
        mimeType,
        videoBitsPerSecond: options.videoBitsPerSecond || DEFAULT_RECORDING_OPTIONS.videoBitsPerSecond
      };

      this.recordedChunks = [];
      this.onDataAvailableCallback = onDataAvailable;
      this.mediaRecorder = new MediaRecorder(stream, recorderOptions);

      // Collect data chunks
      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
          debugLog('[VideoRecorder] Data chunk received:', event.data.size, 'bytes');
        }
      };

      // Handle recording completion
      this.mediaRecorder.onstop = () => {
        debugLog('[VideoRecorder] Recording stopped');
        debugLog('[VideoRecorder] Total chunks:', this.recordedChunks.length);

        const blob = new Blob(this.recordedChunks, { type: mimeType });
        debugLog('[VideoRecorder] Final blob size:', blob.size, 'bytes');

        if (this.onDataAvailableCallback) {
          this.onDataAvailableCallback(blob);
        }
      };

      // Handle errors
      this.mediaRecorder.onerror = (event: Event) => {
        console.error('[VideoRecorder] Recording error:', event);
      };

      // Start recording (collect data every 100ms for smooth streaming)
      this.mediaRecorder.start(100);
      this.startTime = Date.now();

      debugLog('[VideoRecorder] Recording started with options:', recorderOptions);
    } catch (error) {
      console.error('[VideoRecorder] Failed to start recording:', error);
      throw new Error(`Failed to start recording: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      debugLog('[VideoRecorder] Stopping recording...');
      this.mediaRecorder.stop();
    } else {
      console.warn('[VideoRecorder] Cannot stop recording: MediaRecorder is not active');
    }
  }

  /**
   * Stop camera and release resources
   */
  stopCamera(): void {
    if (this.stream) {
      debugLog('[VideoRecorder] Stopping camera...');
      this.stream.getTracks().forEach(track => {
        track.stop();
        debugLog('[VideoRecorder] Stopped track:', track.label);
      });
      this.stream = null;
    }
  }

  /**
   * Get recording duration in milliseconds
   */
  getRecordingDuration(): number {
    if (!this.startTime) return 0;
    return Date.now() - this.startTime;
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.mediaRecorder !== null && this.mediaRecorder.state === 'recording';
  }

  /**
   * Convert Blob to File with proper naming
   * @param blob Video blob
   * @param filename Desired filename (without extension)
   * @returns File object ready for upload
   */
  convertBlobToFile(blob: Blob, filename?: string): File {
    // Determine file extension from MIME type
    let extension = 'webm';
    if (blob.type.includes('mp4')) {
      extension = 'mp4';
    }

    const fileName = filename ? `${filename}.${extension}` : `cribsy-recording-${Date.now()}.${extension}`;

    return new File([blob], fileName, {
      type: blob.type,
      lastModified: Date.now()
    });
  }

  /**
   * Get current camera stream
   */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.stopRecording();
    this.stopCamera();
    this.recordedChunks = [];
    this.mediaRecorder = null;
    this.onDataAvailableCallback = null;
    this.startTime = null;
  }
}

/**
 * Check if camera access is available
 */
export async function checkCameraAvailability(): Promise<{ available: boolean; reason?: string }> {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return {
        available: false,
        reason: 'Camera API not supported in this browser'
      };
    }

    // Check if we have permission
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    if (videoDevices.length === 0) {
      return {
        available: false,
        reason: 'No camera devices found'
      };
    }

    return { available: true };
  } catch (error) {
    return {
      available: false,
      reason: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Format recording duration for display
 * @param milliseconds Duration in milliseconds
 * @returns Formatted string (MM:SS)
 */
export function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get recommended recording duration (3 minutes in milliseconds)
 */
export const RECOMMENDED_RECORDING_DURATION_MS = 3 * 60 * 1000; // 3 minutes
